import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, ShoppingBag, Check, Truck, Store } from "lucide-react";
import { Button } from "../components/ui/button";
import { Seo } from "../components/Seo";
import api from "../lib/api";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const CART_KEY = "iri_shop_cart";

export default function ShopProduct() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setProduct(null); setNotFound(false); setAdded(false);
    api.get("/products").then((r) => {
      const p = r.data.find((x) => x.slug === slug);
      if (p) setProduct(p); else setNotFound(true);
    }).catch(() => setNotFound(true));
  }, [slug]);

  const addToCart = () => {
    let cart = {};
    try { cart = JSON.parse(localStorage.getItem(CART_KEY)) || {}; } catch { cart = {}; }
    cart[slug] = (cart[slug] || 0) + 1;
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
    setAdded(true);
    toast.success("Added to cart");
  };

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="product-not-found">
        <p className="text-[#5C6664]">We couldn't find that product.</p>
        <Link to="/shop" className="mt-4 inline-flex items-center gap-1 text-iri-mid"><ArrowLeft className="h-4 w-4" /> Back to shop</Link>
      </div>
    );
  }
  if (!product) return <div className="py-40 text-center text-[#8A9492]" data-testid="product-loading">Loading…</div>;

  return (
    <div className="pt-28 pb-24" data-testid="shop-product-page">
      <Seo title={product.name} description={product.tagline || product.description?.slice(0, 150)} path={`/shop/${slug}`} />
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <Link to="/shop" data-testid="product-back" className="inline-flex items-center gap-1 text-sm text-[#8A9492] hover:text-ink mb-8">
          <ArrowLeft className="h-4 w-4" /> Back to shop
        </Link>

        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div className="rounded-[1.75rem] overflow-hidden bg-stone aspect-square">
            <img src={product.image} alt={product.name} className="w-full h-full object-contain" data-testid="product-image" />
          </div>

          <div className="md:pt-4">
            {product.brand && <p className="text-xs uppercase tracking-[0.2em] text-iri-mid" data-testid="product-brand">{product.brand}</p>}
            <h1 className="font-heading text-3xl md:text-4xl font-light text-ink mt-2 leading-tight" data-testid="product-name">{product.name}</h1>
            {product.tagline && <p className="text-[#5C6664] mt-3">{product.tagline}</p>}
            <p className="font-heading text-3xl font-light text-ink mt-5" data-testid="product-price">{money(product.price)}</p>

            <div className="mt-6">
              {product.in_stock ? (
                added ? (
                  <div className="flex flex-col sm:flex-row gap-3" data-testid="product-added">
                    <span className="inline-flex items-center gap-2 text-sm text-iri-mid h-12 px-4"><Check className="h-4 w-4" /> Added to cart</span>
                    <Button onClick={() => navigate("/shop")} className="rounded-full iri-gradient text-ink h-12 px-7 gap-2 font-medium" data-testid="product-view-cart">
                      <ShoppingBag className="h-4 w-4" /> View cart & checkout
                    </Button>
                  </div>
                ) : (
                  <Button onClick={addToCart} data-testid="product-add-to-cart" className="rounded-full bg-ink text-white hover:bg-ink/90 h-12 px-8 gap-2 font-medium">
                    <Plus className="h-4 w-4" /> Add to cart
                  </Button>
                )
              ) : (
                <span className="inline-block text-sm text-[#8A9492] bg-stone rounded-full px-5 py-3" data-testid="product-oos">Currently out of stock</span>
              )}
            </div>

            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs text-[#8A9492]">
              <span className="inline-flex items-center gap-1.5"><Store className="h-3.5 w-3.5" /> Free in-spa pickup</span>
              <span className="inline-flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" /> Ships for $15</span>
            </div>

            {product.description && (
              <div className="mt-8 pt-8 border-t border-slate-100">
                <h2 className="font-heading text-lg font-medium text-ink mb-3">About this product</h2>
                <p className="text-[#5C6664] leading-relaxed text-sm" data-testid="product-description">{product.description}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
